import { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Search, FolderCheck } from "lucide-react";
import { API } from "../../context/AuthContext";
import PortalStats from "./PortalStats";
import PortalDropzone from "./PortalDropzone";
import DocumentRow from "./DocumentRow";
import DocumentDetail from "./DocumentDetail";

const STATUSES = ["needs_review", "processing", "approved", "rejected", "exception", "failed", "duplicate", "superseded", "withdrawn"];
const sel = "text-[11px] font-mono rounded-lg px-2 py-1.5 border bg-gray-950 border-white/10 text-gray-300 focus:outline-none focus:border-[#f59e0b]/50";

export default function DocumentPortal({ caseId, canEdit, onChanged }) {
  const [docs, setDocs] = useState([]);
  const [stats, setStats] = useState(null);
  const [templates, setTemplates] = useState([]);
  const [status, setStatus] = useState("all");
  const [template, setTemplate] = useState("all");
  const [q, setQ] = useState("");
  const [openId, setOpenId] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    if (!caseId) return;
    const [d, s] = await Promise.all([
      axios.get(`${API}/cases/${caseId}/documents`).catch(() => ({ data: [] })),
      axios.get(`${API}/cases/${caseId}/exceptions`).catch(() => ({ data: null })),
    ]);
    setDocs(d.data);
    setStats(s.data);
    setLoading(false);
  }, [caseId]);

  useEffect(() => { setLoading(true); load(); }, [load]);
  useEffect(() => { axios.get(`${API}/templates`).then((r) => setTemplates(r.data)).catch(() => {}); }, []);

  const busy = docs.some((d) => d.status === "processing" || d.status === "pending");
  useEffect(() => {
    if (!busy) return;
    const t = setInterval(() => { load(); onChanged?.(); }, 5000);
    return () => clearInterval(t);
  }, [busy, load, onChanged]);

  const visible = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return docs.filter((d) => {
      if (status !== "all" && d.status !== status) return false;
      if (template !== "all" && d.template_id !== template) return false;
      if (!needle) return true;
      return [d.original_name, d.template_display, ...Object.values(d.field_values || {})].join(" ").toLowerCase().includes(needle);
    });
  }, [docs, status, template, q]);

  const refreshAll = () => { load(); onChanged?.(); };

  return (
    <div className="bg-[#131f3a] border border-white/5 rounded-2xl p-5 sm:p-6 space-y-5 min-h-[560px]" data-testid="document-portal">
      <div className="flex items-start gap-3">
        <FolderCheck className="w-5 h-5 text-[#f59e0b] mt-0.5" />
        <div>
          <h3 className="font-display font-bold text-base uppercase tracking-wide text-white">Firm Document Portal</h3>
          <p className="text-xs text-gray-400 mt-0.5">Every upload is validated, OCR'd, classified against the firm templates, cited page-by-page and held for human review before it counts as complete.</p>
        </div>
      </div>
      <PortalStats stats={stats} onPick={setStatus} />
      {canEdit && <PortalDropzone caseId={caseId} onQueued={() => { load(); onChanged?.(); }} />}
      <div className="flex flex-wrap items-center gap-2">
        <div className="relative flex-1 min-w-[180px]">
          <Search className="w-3.5 h-3.5 text-gray-500 absolute left-2.5 top-1/2 -translate-y-1/2" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search names and extracted fields (e.g. 5678, ACME, 24FL)…" data-testid="portal-search-input"
            className="w-full bg-gray-950 border border-white/10 rounded-lg pl-8 pr-3 py-1.5 text-xs text-gray-200 placeholder:text-gray-600 focus:outline-none focus:border-[#f59e0b]/50" />
        </div>
        <select value={status} onChange={(e) => setStatus(e.target.value)} className={sel} data-testid="portal-status-filter">
          <option value="all">All statuses</option>
          {STATUSES.map((s) => <option key={s} value={s}>{s.replace("_", " ")}</option>)}
        </select>
        <select value={template} onChange={(e) => setTemplate(e.target.value)} className={`${sel} max-w-[200px]`} data-testid="portal-template-filter">
          <option value="all">All categories</option>
          {templates.map((t) => <option key={t.template_id} value={t.template_id}>{t.display_name}</option>)}
          <option value="general_document">General Document</option>
        </select>
      </div>
      <div className="space-y-2" data-testid="portal-doc-list">
        {loading ? <div className="text-xs font-mono text-[#f59e0b] py-10 text-center animate-pulse">Loading the document portal…</div>
          : visible.length === 0 ? <div className="text-xs italic text-gray-500 py-10 text-center" data-testid="portal-empty">{docs.length ? "No documents match these filters." : "No portal documents yet — drop statements, orders, returns or paystubs above."}</div>
            : visible.map((d) => <DocumentRow key={d.document_id} doc={d} onOpen={(x) => setOpenId(x.document_id)} />)}
      </div>
      {openId && <DocumentDetail documentId={openId} caseId={caseId} canEdit={canEdit} onClose={() => setOpenId(null)} onChanged={refreshAll} onOpenDoc={setOpenId} />}
    </div>
  );
}
